// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// Debounced search box for the Activity panel. The user types free text (matched
// client-side against person name / file name); after a short pause the committed
// term is reported upward. A suggestion dropdown groups matching Users and File
// Names, and a clear (x) control empties the box.
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Tag,
  Text,
} from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { BiX } from 'react-icons/bi';

import { SearchSuggestions } from './activity-filtering';

type Props = {
  // The committed search term applied to the list.
  value: string;
  // Reports the debounced committed term upward.
  onChange: (term: string) => void;
  // Builds suggestions for the currently typed (raw) term.
  buildSuggestions: (rawTerm: string) => SearchSuggestions;
};

const DEBOUNCE_MS = 250;

export const ActivitySearch: FC<Props> = ({
  value,
  onChange,
  buildSuggestions,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<SearchSuggestions>({
    users: [],
    files: [],
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the local input in sync when the committed value is reset externally (Clear).
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounce committing the term to the list so we don't refilter on every keystroke.
  const debouncedCommit = useRef(
    debounce((term: string) => {
      onChange(term);
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => {
      debouncedCommit.cancel();
    };
  }, [debouncedCommit]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const term = e.currentTarget.value;
    setInputValue(term);
    setSuggestions(buildSuggestions(term));
    setShowSuggestions(term.trim().length > 0);
    debouncedCommit(term.trim());
  };

  const handleClear = () => {
    debouncedCommit.cancel();
    setInputValue('');
    setShowSuggestions(false);
    onChange('');
    inputRef.current?.focus();
  };

  const handlePick = (term: string) => {
    debouncedCommit.cancel();
    setInputValue(term);
    setShowSuggestions(false);
    onChange(term);
  };

  const hasSuggestions =
    suggestions.users.length > 0 || suggestions.files.length > 0;

  return (
    <Box position="relative">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={inputValue}
          placeholder="Search by user or file name..."
          onChange={handleChange}
          onFocus={() => setShowSuggestions(inputValue.trim().length > 0)}
          data-testid="activity-search-input"
        />
        {inputValue !== '' && (
          <InputRightElement>
            <IconButton
              variant="ghost"
              icon={<BiX fontSize="20" />}
              aria-label="Clear search"
              onClick={handleClear}
              data-testid="activity-search-clear"
            />
          </InputRightElement>
        )}
      </InputGroup>

      {showSuggestions && hasSuggestions && (
        <Box
          position="absolute"
          zIndex={2}
          mt={1}
          w="100%"
          bg="white"
          boxShadow="md"
          borderRadius="md"
          borderWidth="1px"
          maxH="64"
          overflowY="auto"
          data-testid="activity-search-suggestions"
        >
          {suggestions.users.length > 0 && (
            <List>
              <ListItem px={3} py={1}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500">
                  Users
                </Text>
              </ListItem>
              {suggestions.users.map((user) => (
                <ListItem
                  key={`user-${user}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handlePick(user)}
                  data-testid="activity-search-suggestion-user"
                >
                  <Tag size="sm" colorScheme="blue" mr={2}>
                    User
                  </Tag>
                  {user}
                </ListItem>
              ))}
            </List>
          )}
          {suggestions.files.length > 0 && (
            <List>
              <ListItem px={3} py={1}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500">
                  File Names
                </Text>
              </ListItem>
              {suggestions.files.map((file) => (
                <ListItem
                  key={`file-${file}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handlePick(file)}
                  data-testid="activity-search-suggestion-file"
                >
                  <Tag size="sm" colorScheme="purple" mr={2}>
                    File
                  </Tag>
                  {file}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};
